import { AppDetailsUrlQueryParams } from "@dashboard/extensions/urls";
import { useAllFlags } from "@dashboard/featureFlags";
import { CircularProgress } from "@material-ui/core";
import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { AppIFrame } from "./AppIFrame";
import { useStyles } from "./styles";
import { useAppActions } from "./useAppActions";
import { useAppDashboardUpdates } from "./useAppDashboardUpdates";
import useTokenRefresh from "./useTokenRefresh";
import { useUpdateAppToken } from "./useUpdateAppToken";

interface Props {
  src: string;
  appToken: string;
  appId: string;
  className?: string;
  params?: AppDetailsUrlQueryParams;
  refetch?: () => void;
  dashboardVersion: string;
  coreVersion?: string;
  onError?: () => void;
  onHandshakeDone?: () => void;
}

const getOrigin = (url: string) => new URL(url).origin;
const APP_READY_TIMEOUT_MS = 5000;

export const AppFrame = ({
  src,
  appToken,
  appId,
  className,
  params,
  onError,
  onHandshakeDone,
  refetch,
  dashboardVersion,
  coreVersion = "",
}: Props) => {
  const frameRef = React.useRef<HTMLIFrameElement | null>(null);
  const readyErrorTimeoutRef = useRef<number | null>(null);
  const hasReportedReadyErrorRef = useRef(false);
  const classes = useStyles();
  const appOrigin = getOrigin(src);
  const flags = useAllFlags();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  /**
   * React on messages from App. The initial handshake is triggered from
   * the app's notifyReady event, once the app can receive dashboard events.
   */
  const { postToExtension, handshakeDone } = useAppActions(
    frameRef.current,
    appOrigin,
    appId,
    appToken,
    {
      core: coreVersion,
      dashboard: dashboardVersion,
    },
  );

  /**
   * Listen to Dashboard context like theme or locale and inform app about it
   */
  useAppDashboardUpdates(frameRef.current, appOrigin, handshakeDone, appId);
  useTokenRefresh(appToken, refetch);

  useUpdateAppToken({
    postToExtension,
    appToken,
    /**
     * If app is not ready, ignore this flow
     */
    enabled: handshakeDone,
  });

  useEffect(() => {
    if (handshakeDone) {
      onHandshakeDone?.();
    }
  }, [handshakeDone, onHandshakeDone]);

  const handleLoad = useCallback(() => {
    setIframeLoaded(true);
  }, []);

  useEffect(() => {
    setIframeLoaded(false);
    hasReportedReadyErrorRef.current = false;

    if (readyErrorTimeoutRef.current !== null) {
      window.clearTimeout(readyErrorTimeoutRef.current);
      readyErrorTimeoutRef.current = null;
    }
  }, [src]);

  useEffect(() => {
    if (!iframeLoaded || handshakeDone || !onError || hasReportedReadyErrorRef.current) {
      return;
    }

    readyErrorTimeoutRef.current = window.setTimeout(() => {
      if (!handshakeDone && !hasReportedReadyErrorRef.current) {
        hasReportedReadyErrorRef.current = true;
        onError();
      }
    }, APP_READY_TIMEOUT_MS);

    return () => {
      if (readyErrorTimeoutRef.current !== null) {
        window.clearTimeout(readyErrorTimeoutRef.current);
        readyErrorTimeoutRef.current = null;
      }
    };
  }, [handshakeDone, iframeLoaded, onError]);

  return (
    <>
      {!iframeLoaded && !handshakeDone && (
        <div className={classes.loader}>
          <CircularProgress color="primary" />
        </div>
      )}
      <AppIFrame
        ref={frameRef}
        src={src}
        appId={appId}
        featureFlags={flags}
        params={params}
        onLoad={handleLoad}
        onError={onError}
        className={clsx(classes.iframe, className, {
          [classes.iframeHidden]: !handshakeDone && !iframeLoaded,
        })}
      />
    </>
  );
};
